export const capacityHero = {
  badge: "Training Programmes",
  title: "Comprehensive Training Programs for Industry Transformation",
  subtitle:
    "Seven programme categories spanning Innovation Black Belt, Factory Skills Development, Professional Skill Development, Sustainability Management, InternPro, PPI Workshops, and Other Trainings. Each programme is designed for real factory outcomes, with hands-on projects, factory demonstrations, and measurable KPIs.",
  ctaPrimary: "Enrol a Cohort",
  ctaSecondary: "Explore the Programmes",
};

export const capacityWhatIs = {
  title: "What is Training?",
  paragraphs: [
    "TIE's Training programme is a structured professional certification system that transforms how textile and apparel factories develop, deploy, and sustain innovation talent. Unlike one-off training workshops, it is a progressive journey across seven distinct programme categories, each building specialised capabilities for different roles and career stages.",
    "The programme combines classroom instruction, factory-floor projects with live demonstrations, peer assessment, and continuous mentorship. Participants do not just learn theory - they apply it in live production environments, observe real machinery and processes, present evidence of impact, and earn credentials that are recognized across the TIE ecosystem and by buyer partners.",
    "Training is the human infrastructure behind every successful PIIC and PTIM deployment. Without trained facilitators, circle leaders, and certified coaches, innovation programmes decay when external support withdraws. This programme ensures capability stays inside the factory.",
  ],
  highlights: [
    { label: "Total Courses", value: "65+" },
    { label: "Programme Categories", value: "7" },
    { label: "Factory Projects", value: "6+" },
    { label: "Certified Practitioners", value: "120+" },
  ],
};

export const capacityTiers = [
  {
    code: "FSD",
    name: "Factory Skills Development",
    level: "Foundation",
    duration: "22 two-day courses",
    audience: "Factory floor operators, technicians, junior engineers",
    description:
      "FSD is the foundational certification for factory-level innovation practitioners. It equips participants with the core tools of structured problem identification, baseline data collection, and simple pilot execution through 22 intensive two-day courses, many including live factory-floor demonstrations.",
    modules: [
      "Innovation mindset & problem awareness",
      "Data collection & baseline documentation",
      "Root-cause analysis fundamentals",
      "Pilot design & basic iteration",
      "Evidence presentation & storytelling",
    ],
    outcome:
      "Graduates can participate effectively in Innovation Circles, collect reliable data, and contribute ideas that are testable on the factory floor.",
    icon: "ri-tools-line",
  },
  {
    code: "PSD",
    name: "Professional Skills Development",
    level: "Management",
    duration: "31 six-hour modules",
    audience: "Engineers, supervisors, mid-level managers, circle leaders",
    description:
      "PSD builds on FSD to develop the professional skills needed to lead innovation projects independently. Participants learn advanced facilitation, cross-functional coordination, and structured validation methods through 31 focused six-hour modules.",
    modules: [
      "Advanced facilitation & circle leadership",
      "Cross-functional coordination & stakeholder management",
      "Experimental design & control variables",
      "ROI calculation & business case development",
      "SOP drafting & scale-up planning",
    ],
    outcome:
      "Graduates can lead Innovation Circles, design controlled pilots, calculate business cases, and draft SOPs for validated solutions.",
    icon: "ri-user-star-line",
  },
  {
    code: "Black Belt",
    name: "Innovation Black Belt",
    level: "Strategic",
    duration: "12 twelve-hour specialisations",
    audience: "Senior managers, heads of department, innovation officers, consultants",
    description:
      "The Innovation Black Belt is the apex certification for strategic innovation leadership. It covers governance design, ecosystem orchestration, advanced analytics, and the ability to architect factory-wide transformation programmes through 12 specialised twelve-hour courses.",
    modules: [
      "Innovation governance & institutional design",
      "Ecosystem orchestration & partner management",
      "Advanced statistical analysis & prediction",
      "Transformation programme architecture",
      "Mentorship & coach-the-coach certification",
    ],
    outcome:
      "Graduates can design and run factory-wide innovation programmes, mentor FSD and PSD candidates, and represent the factory in ecosystem-level initiatives.",
    icon: "ri-vip-crown-line",
  },
];

export const capacityProcessSteps = [
  {
    phase: "Phase 1",
    week: "Weeks 1–4",
    title: "Assessment & Cohort Formation",
    description:
      "Factories nominate candidates who undergo a skills baseline assessment. Cohorts are formed with a mix of departments and experience levels to maximize peer learning.",
    activities: [
      "Nomination & baseline skills assessment",
      "Cohort grouping & role mapping",
      "Learning path customization",
      "Mentor assignment & kickoff",
    ],
  },
  {
    phase: "Phase 2",
    week: "Weeks 5–12",
    title: "Structured Learning & Factory Projects",
    description:
      "Participants attend modular training sessions while simultaneously running live factory projects. Every module has a practical assignment tied to real production outcomes.",
    activities: [
      "Module-based classroom & virtual sessions",
      "Parallel factory project execution",
      "Weekly mentor review & feedback",
      "Peer assessment & circle presentations",
    ],
  },
  {
    phase: "Phase 3",
    week: "Weeks 13–16",
    title: "Validation & Certification",
    description:
      "Projects are validated against evidence criteria. Participants present their work to an assessment panel comprising TIE faculty, factory leadership, and external reviewers.",
    activities: [
      "Evidence compilation & project portfolio",
      "Panel presentation & Q&A",
      "Third-party assessment & scoring",
      "Certification ceremony & repository entry",
    ],
  },
  {
    phase: "Phase 4",
    week: "Ongoing",
    title: "Alumni Network & Continuous Development",
    description:
      "Certified practitioners join the TIE alumni network with access to refresher courses, advanced workshops, expert office hours, and priority consideration for ecosystem roles.",
    activities: [
      "Alumni network access & community events",
      "Annual refresher & recertification",
      "Expert office hours & advisory support",
      "Ecosystem role & speaking opportunities",
    ],
  },
];

export const capacityOutcomes = {
  title: "What Every Cohort Produces",
  items: [
    {
      title: "Certified Practitioners",
      description:
        "Every graduate receives a TIE-recognized certificate at their level, with a verifiable credential in the TIE practitioner registry.",
      icon: "ri-award-line",
    },
    {
      title: "Live Factory Projects",
      description:
        "Each participant delivers at least one completed innovation project with baseline, endline, and evidence documentation from their own factory.",
      icon: "ri-building-2-line",
    },
    {
      title: "Internal Trainers",
      description:
        "Black Belt graduates become internal trainers, enabling the factory to run its own FSD and PSD programmes without external dependency.",
      icon: "ri-presentation-line",
    },
    {
      title: "SOP Library",
      description:
        "Validated solutions from factory projects are compiled into a factory-specific SOP library, creating reusable institutional knowledge.",
      icon: "ri-book-2-line",
    },
    {
      title: "Talent Pipeline",
      description:
        "A clear career pathway from operator to innovation leader, improving retention and making the factory attractive to top engineering talent.",
      icon: "ri-route-line",
    },
    {
      title: "Ecosystem Recognition",
      description:
        "Certified practitioners are listed in the TIE public registry, visible to buyers, investors, and partner institutions seeking verified innovation talent.",
      icon: "ri-global-line",
    },
  ],
};

export const capacityStats = [
  { value: "65+", label: "Total Courses", sub: "across seven programme categories" },
  { value: "120+", label: "Certified Practitioners", sub: "across 40+ factories" },
  { value: "7", label: "Programme Categories", sub: "FSD, PSD, Black Belt, SMS, InternPro, PPI, Other" },
  { value: "96%", label: "Completion Rate", sub: "cohorts finish with certification" },
];